import { LOCALE_ID, NgModule } from '@angular/core';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withInterceptors, withFetch } from '@angular/common/http';
import { registerLocaleData, DatePipe } from '@angular/common';
import { provideRouter, RouterLink, RouterOutlet, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { NgbAccordionModule, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import localeDeAt from '@angular/common/locales/de-AT';
registerLocaleData(localeDeAt);
import { HttpErrorInterceptor, CacheHeadersInterceptor } from '@app/interceptors';
import {
    TranslationService,
    NotificationService,
    AddressService,
    CountryService,
    NewsletterService
} from '@app/services';
import { AppComponent } from '@app/app.component';
import { ProgressSpinnerComponent } from '@app/components/common/progress-spinner/progress-spinner.component';
import { ConfirmationDialogComponent } from '@app/components/common/confirmation-dialog/confirmation-dialog.component';
import { InformationDialogComponent } from '@app/components/common/information-dialog/information-dialog.component';
import { WelcomeDialogComponent } from '@app/components/common/welcome-dialog/welcome-dialog.component';
import { MyMachinesOverviewComponent } from '@app/pages/mymachines-overview/mymachines-overview.component';
import { MachineDetailsComponent } from './components/machine-details/machine-details.component';
import { CountrySelectionComponent } from '@app/pages/country-selector/country-selection.component';
import { ParametersService } from './components/parameters/parameters.service';
import { MachinesearchComponent } from '@app/pages/machinesearch/machinesearch.component';
import { MachineService } from './services/machine.service';
import { QrscannerComponent } from './components/poetpro/qrscanner/qrscanner.component';
import { ApplicationInsightsService } from './services/application-insights-service.service';
import { MachinepageComponent } from './components/poetpro/machinepage/machinepage.component';
import { MachineInfosService } from './components/machine-infos/machine-infos.service';
import { ProductRangeService } from './components/poetpro/productrange/productrange.service';
import { HighlightsAndDataComponent } from './components/poetpro/highlights-and-data/highlights-and-data.component';
import { MedienComponent } from './components/poetpro/medien/medien.component';
import { ZmediasComponent } from './components/poetpro/zmedias/zmedias.component';
import { FitmentsComponent } from './components/poetpro/fitments/fitments.component';
import { ExtractFileNamePipe } from './pipes/extractFileName.pipe';
import { LoadScriptDirective } from './directives/load-script.directive';

import { appRoutes } from '@app/app.routes';
import { LauncherComponent } from './pages/launcher/launcher.component';
import { ProfileOverviewComponent } from './pages/profile-overview/profile-overview.component';
import { FooterComponent } from './components/footer/footer.component';
import { BreadcrumbsComponent } from './components/breadcrumbs/breadcrumbs.component';
import { TileComponent } from './components/tile/tile.component';
import { HighlightCardsComponent } from './components/highlight-cards/highlight-cards.component';
import { PlaygroundComponent } from './components/playground/playground.component';
import { NewsletterComponent } from './components/newsletter/newsletter.component';
import { PromptMachineNumberModalComponent } from './components/prompt-machine-number-modal/prompt-machine-number-modal.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { LoginDialogComponent } from './components/common/login-dialog/login-dialog.component';
import { SupportComponent } from './pages/support/support.component';
import { FaqComponent } from './pages/faq/faq.component';
import { UniquePipe } from './pipes/unique.pipe';
import { PoeModalService } from './services/poe-modal.service';
import { HaytoolAssistComponent } from './components/haytool-assist/haytool-assist.component';
import { TramlineAssistComponent } from './components/tramline-assist/tramline-assist.component';
import { NgxPoettingerComponentsModule } from '@it-entwicklung/ngx-poettinger-components';
import { NgxPoettingerNotificationsModule } from '@it-entwicklung/ngx-poettinger-notifications';
import { ODataTransformerInterceptor } from './interceptors/o-data-transformer.interceptor';
import { RowCropAssistComponent } from './components/row-crop-assist/row-crop-assist.component';
import { MatSidenavModule } from '@angular/material/sidenav';
import { IonicModule } from '@ionic/angular';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { InputDialogComponent } from './components/common/input-dialog/input-dialog.component';
import { TelemetryStatusCircleComponent } from './components/telemetry-status-circle/telemetry-status-circle.component';
import { SliderComponent } from './components/slider/slider.component';
import { SlideDirective } from './components/slider/slide.directive';
import { CdkObserveContent } from '@angular/cdk/observers';
import { TitlebarComponent } from './components/titlebar/titlebar.component';
import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';
import { environment } from 'environments/environment';
import { LoginButtonDirective } from './directives/login-button.directive';
import { RegisterButtonDirective } from './directives/register-button.directive';
import { IfAuthenticatedDirective } from '@app/directives/if-authenticated.directive';
import { AuthPromptComponent } from './components/auth-prompt/auth-prompt.component';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { UserManagerService } from './managers';
import { UserInputComponent } from './components/user-input/user-input.component';
import { ProductSearchComponent } from './components/product-search/product-search.component';
import { EmbeddedSvgComponent } from "@app/components/embedded-svg/embedded-svg.component";

export const httpLoaderFactory = (http: HttpClient) => new TranslateHttpLoader(http, './assets/i18n/', '.json');

@NgModule({
    declarations: [
        AppComponent,
        ConfirmationDialogComponent,
        InformationDialogComponent,
        MyMachinesOverviewComponent,
        CountrySelectionComponent,
        QrscannerComponent,
        LoadScriptDirective,
        LauncherComponent,
        ProfileOverviewComponent,
        FooterComponent,
        PromptMachineNumberModalComponent,
        LoginDialogComponent,
        SupportComponent,
        HaytoolAssistComponent,
        TramlineAssistComponent,
        RowCropAssistComponent,
        InputDialogComponent
    ],
    bootstrap: [AppComponent],
    imports: [
        MachineDetailsComponent,
        FaqComponent,
        UniquePipe,
        TileComponent,
        TelemetryStatusCircleComponent,
        WelcomeDialogComponent,
        NgbAccordionModule,
        HighlightCardsComponent,
        HighlightsAndDataComponent,
        MedienComponent,
        ZmediasComponent,
        FitmentsComponent,
        ExtractFileNamePipe,
        MatSidenavModule,
        UserInputComponent,
        BrowserModule,
        BreadcrumbsComponent,
        RouterLink,
        ProgressSpinnerComponent,
        NewsletterComponent,
        SliderComponent,
        MachinesearchComponent,
        SlideDirective,
        PlaygroundComponent,
        RouterOutlet,
        TitlebarComponent,
        MachinepageComponent,
        FormsModule,
        ReactiveFormsModule,
        NgbModule,
        DragDropModule,
        ProductSearchComponent,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: httpLoaderFactory,
                deps: [HttpClient]
            },
        }),
        NgxPoettingerComponentsModule,
        NgxPoettingerNotificationsModule,
        IonicModule.forRoot({}),
        CdkObserveContent,
        LoginButtonDirective,
        RegisterButtonDirective,
        IfAuthenticatedDirective,
        AuthPromptComponent,
        EmbeddedSvgComponent
    ],
    providers: [
        { provide: LOCALE_ID, useValue: 'de-at' },
        { provide: HTTP_INTERCEPTORS, useClass: ODataTransformerInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: CacheHeadersInterceptor, multi: true },
        // { provide: HTTP_INTERCEPTORS, useClass: AuthHeaderInterceptor, multi: true, deps: [AuthService] },
        provideAnimations(),
        TranslationService,
        NotificationService,
        UserManagerService,
        CountryService,
        ParametersService,
        MachineService,
        DatePipe,
        ApplicationInsightsService,
        CookieService,
        MachineInfosService,
        ProductRangeService,
        AddressService,
        NewsletterService,
        PoeModalService,
        UniquePipe,
        provideHttpClient(withFetch(), withInterceptorsFromDi(), withInterceptors([authHttpInterceptorFn])),
        provideIonicAngular({
            mode: 'ios',
            useSetInputAPI: true
        }),
        provideRouter(appRoutes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }), withComponentInputBinding()),
        provideAuth0({
            domain: environment.authDomain,
            clientId: environment.authClientID,
            authorizationParams: {
                redirect_uri: `${globalThis.location.origin}/auth/callback`,
                audience: environment.authAudience,
                scope: 'openid profile email'
            },
            cacheLocation: 'localstorage',
            httpInterceptor: {
                allowedList: [
                    {
                        uri: `${environment.apiPath}/*`,
                        allowAnonymous: true,
                        tokenOptions: {
                            authorizationParams: {
                                audience: environment.authAudience
                            }
                        }
                    }
                ]
            }
        }),
    ]
})
export class AppModule { }
